import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { sendAdminEmail } from '../../services/bookingService';
import AppIcon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SmartSelect from '../../components/ui/SmartSelect';

const TICKET_STATUSES = {
  open: { label: 'مفتوحة', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'قيد المعالجة', color: 'bg-amber-100 text-amber-700' },
  waiting_user: { label: 'بانتظار ردك', color: 'bg-purple-100 text-purple-700' },
  waiting_admin: { label: 'بانتظار الدعم', color: 'bg-orange-100 text-orange-700' },
  resolved: { label: 'تم الحل', color: 'bg-green-100 text-green-700' },
  closed: { label: 'مغلقة', color: 'bg-slate-100 text-slate-700' },
};

const TICKET_TYPES = {
  support: {
    label: 'طلب دعم',
    desc: 'استفسار أو مساعدة تقنية أو إدارية',
    icon: 'Headphones',
    active: 'border-blue-500 bg-blue-50',
    badge: 'bg-blue-100 text-blue-700',
  },
  complaint: {
    label: 'شكوى',
    desc: 'مشكلة في الحجز أو الدفع أو العميل',
    icon: 'AlertCircle',
    active: 'border-amber-500 bg-amber-50',
    badge: 'bg-amber-100 text-amber-700',
  },
};

const ISSUE_TYPES = {
  support: [
    { value: 'payment', label: 'استفسار عن المستحقات' },
    { value: 'account', label: 'مشكلة في الحساب أو التحقق' },
    { value: 'technical', label: 'مشكلة تقنية في المنصة' },
    { value: 'booking', label: 'مساعدة في الحجز' },
    { value: 'other', label: 'أخرى' },
  ],
  complaint: [
    { value: 'payment', label: 'مشكلة في الدفع أو المستحقات' },
    { value: 'booking', label: 'مشكلة في تفاصيل الحجز' },
    { value: 'complaint', label: 'شكوى بشأن عميل أو إلغاء غير مبرر' },
    { value: 'photographer', label: 'شكوى تتعلق بمصور' },
    { value: 'other', label: 'أخرى' },
  ],
};

const Support = () => {
  const [myTickets, setMyTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState(null);
  const [form, setForm] = useState({ ticketType: '', category: '', message: '' });

  // Ticket detail / conversation
  const [openTicket, setOpenTicket]   = useState(null);   // selected ticket object
  const [replies, setReplies]         = useState([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [replyText, setReplyText]     = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // شكاوى العملاء على حجوزات هذا المصور
  const [customerComplaints, setCustomerComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [openComplaint, setOpenComplaint] = useState(null);
  const [complaintReplies, setComplaintReplies] = useState([]);
  const [complaintReplyText, setComplaintReplyText] = useState('');
  const [sendingComplaintReply, setSendingComplaintReply] = useState(false);

  useEffect(() => { loadMyTickets(); loadCustomerComplaints(); }, []);

  const loadMyTickets = async () => {
    try {
      setTicketsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (!error) setMyTickets(data || []);
    } catch (e) {
      console.error('Failed to load tickets:', e);
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    if (openTicket) {
      document.body.classList.add('ticket-modal-open');
    } else {
      document.body.classList.remove('ticket-modal-open');
    }
    return () => document.body.classList.remove('ticket-modal-open');
  }, [openTicket]);

  // Realtime: show new admin replies instantly
  useEffect(() => {
    if (!openTicket?.id) return;
    const channel = supabase
      .channel(`support-replies-${openTicket.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'ticket_replies', filter: `ticket_id=eq.${openTicket.id}` },
        async () => {
          const { data } = await supabase
            .from('ticket_replies')
            .select('*')
            .eq('ticket_id', openTicket.id)
            .eq('is_internal', false)
            .order('created_at', { ascending: true });
          setReplies(data || []);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [openTicket?.id]);

  const openTicketDetail = async (ticket) => {
    setOpenTicket(ticket);
    setReplies([]);
    setReplyText('');
    setRepliesLoading(true);
    try {
      const { data, error } = await supabase
        .from('ticket_replies')
        .select('*')
        .eq('ticket_id', ticket.id)
        .eq('is_internal', false)
        .order('created_at', { ascending: true });
      if (!error) setReplies(data || []);
    } catch (e) {
      console.error('Failed to load replies:', e);
    } finally {
      setRepliesLoading(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !openTicket) return;
    setSendingReply(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('ticket_replies').insert({
        ticket_id: openTicket.id,
        user_id: user.id,
        message: replyText.trim(),
        is_internal: false,
      });
      if (error) throw error;
      setReplyText('');
      // Reload replies
      const { data } = await supabase
        .from('ticket_replies')
        .select('*')
        .eq('ticket_id', openTicket.id)
        .eq('is_internal', false)
        .order('created_at', { ascending: true });
      setReplies(data || []);
    } catch (e) {
      alert('فشل إرسال الرد: ' + (e.message || 'حاول مرة أخرى'));
    } finally {
      setSendingReply(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('يرجى تسجيل الدخول أولاً');

      const typeInfo = TICKET_TYPES[form.ticketType];
      const catList = ISSUE_TYPES[form.ticketType] || [];
      const catLabel = catList.find((c) => c.value === form.category)?.label || '';
      const subject = catLabel
        ? `${typeInfo?.label}: ${catLabel}`
        : typeInfo?.label || 'طلب دعم';

      const { data, error } = await supabase
        .from('support_tickets')
        .insert({
          user_id: user.id,
          user_type: 'photographer',
          ticket_type: form.ticketType,
          subject,
          description: form.message,
          category: form.category || 'other',
          priority: 'medium',
          status: 'open',
        })
        .select('ticket_number, ticket_type')
        .single();

      if (error) throw error;

      setSubmittedTicket(data);
      setForm({ ticketType: '', category: '', message: '' });
      loadMyTickets();

      // In-app confirmation notification for the photographer (non-blocking)
      ;(async () => {
        try {
          await supabase.from('notifications').insert({
            user_id: user.id,
            type: 'support',
            title: '✅ تم استلام تذكرتك',
            message: `تم استلام تذكرتك رقم ${data.ticket_number || ''}. سيراجعها فريق الدعم ويرد عليك قريباً.`,
            is_read: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        } catch (_) {}
      })();

      // Notify admin via email (non-blocking)
      supabase.auth.getUser().then(({ data: { user: cu } }) =>
        supabase.from('user_profiles').select('full_name').eq('id', cu?.id).single()
      ).then(({ data: prof }) => sendAdminEmail({
        templateKey: 'admin_photographer_support',
        templateData: {
          ticket_number: data.ticket_number || '',
          user_name: prof?.full_name || '',
          subject,
          category: form.category || 'other',
          description: form.message,
        },
      })).catch(() => {});
    } catch (e) {
      console.error('Failed to submit ticket:', e);
      alert('فشل إرسال الطلب: ' + (e.message || 'حاول مرة أخرى'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewTicket = () => {
    setSubmittedTicket(null);
    setForm({ ticketType: '', category: '', message: '' });
  };

  const loadCustomerComplaints = async () => {
    setComplaintsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // جلب حجوزات المصور لمعرفة الـ booking_ids
      const { data: bookings } = await supabase
        .from('bookings')
        .select('id')
        .eq('photographer_id', user.id);

      if (!bookings || bookings.length === 0) { setComplaintsLoading(false); return; }

      const bookingIds = bookings.map((b) => b.id);

      // جلب تذاكر الشكاوى المرتبطة بحجوزات هذا المصور من العملاء
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('ticket_type', 'complaint')
        .eq('user_type', 'customer')
        .in('related_booking_id', bookingIds)
        .order('created_at', { ascending: false });

      if (!error) setCustomerComplaints(data || []);
    } catch (e) {
      console.error('Failed to load customer complaints:', e);
    } finally {
      setComplaintsLoading(false);
    }
  };

  const openComplaintDetail = async (ticket) => {
    setOpenComplaint(ticket);
    setComplaintReplies([]);
    setComplaintReplyText('');
    try {
      const { data } = await supabase
        .from('ticket_replies')
        .select('*')
        .eq('ticket_id', ticket.id)
        .eq('is_internal', false)
        .order('created_at', { ascending: true });
      setComplaintReplies(data || []);
    } catch (e) {
      console.error('Failed to load complaint replies:', e);
    }
  };

  const sendComplaintReply = async () => {
    if (!complaintReplyText.trim() || !openComplaint) return;
    setSendingComplaintReply(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('ticket_replies').insert({
        ticket_id: openComplaint.id,
        user_id: user.id,
        message: complaintReplyText.trim(),
        is_internal: false,
      });
      if (error) throw error;
      setComplaintReplyText('');
      const { data } = await supabase
        .from('ticket_replies')
        .select('*')
        .eq('ticket_id', openComplaint.id)
        .eq('is_internal', false)
        .order('created_at', { ascending: true });
      setComplaintReplies(data || []);
    } catch (e) {
      alert('فشل إرسال الرد: ' + (e.message || 'حاول مرة أخرى'));
    } finally {
      setSendingComplaintReply(false);
    }
  };

  const currentCategories = ISSUE_TYPES[form.ticketType] || [];

  return (
    <>
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">الدعم والشكاوى</h1>
        <p className="text-slate-600">تواصل مع فريق الدعم لأي مشكلة أو استفسار</p>
      </div>

      {/* Submit Ticket */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-6">تقديم طلب</h2>

        {/* Success State */}
        {submittedTicket ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AppIcon name="CheckCircle" size={36} className="text-success" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-1">
              تم إرسال {submittedTicket.ticket_type === 'complaint' ? 'شكواك' : 'طلبك'} بنجاح
            </h3>
            <p className="text-slate-500 text-sm mb-6">سيتواصل معك فريق الدعم قريباً</p>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mb-6 inline-block min-w-[260px]">
              <p className="text-xs text-slate-500 mb-2">رقم التذكرة — احتفظ به كمرجع</p>
              <p className="text-2xl font-mono font-bold text-primary tracking-widest">
                {submittedTicket.ticket_number}
              </p>
              <span className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                submittedTicket.ticket_type === 'complaint'
                  ? TICKET_TYPES.complaint.badge
                  : TICKET_TYPES.support.badge
              }`}>
                <AppIcon
                  name={submittedTicket.ticket_type === 'complaint' ? 'AlertCircle' : 'Headphones'}
                  size={12}
                />
                {TICKET_TYPES[submittedTicket.ticket_type]?.label}
              </span>
            </div>

            <div>
              <Button variant="outline" onClick={handleNewTicket} iconName="Plus" iconPosition="right">
                تقديم طلب جديد
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Step 1: Type */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                ١. اختر نوع الطلب
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(TICKET_TYPES).map(([key, info]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm({ ...form, ticketType: key, category: '' })}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-right ${
                      form.ticketType === key
                        ? info.active + ' shadow-sm'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      form.ticketType === key ? 'bg-white/70' : 'bg-slate-100'
                    }`}>
                      <AppIcon
                        name={info.icon}
                        size={18}
                        className={form.ticketType === key
                          ? key === 'complaint' ? 'text-amber-600' : 'text-blue-600'
                          : 'text-slate-500'}
                      />
                    </div>
                    <div>
                      <p className={`font-bold text-sm ${
                        form.ticketType === key
                          ? key === 'complaint' ? 'text-amber-700' : 'text-blue-700'
                          : 'text-slate-800'
                      }`}>{info.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{info.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Steps 2-3 appear after type selection */}
            {form.ticketType && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    ٢. التصنيف
                  </label>
                  <SmartSelect
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    options={currentCategories}
                    placeholder="اختر التصنيف"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    ٣. التفاصيل
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                    rows={5}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    placeholder={
                      form.ticketType === 'complaint'
                        ? 'صف الشكوى بالتفصيل...'
                        : 'اشرح ما تحتاجه بالتفصيل...'
                    }
                  />
                </div>

                <Button
                  type="submit"
                  variant="default"
                  iconName="Send"
                  iconPosition="right"
                  loading={submitting}
                  disabled={!form.category || !form.message.trim()}
                  className="w-full sm:w-auto"
                >
                  {form.ticketType === 'complaint' ? 'إرسال الشكوى' : 'إرسال الطلب'}
                </Button>
              </>
            )}
          </form>
        )}
      </div>

      {/* My Tickets */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">طلباتي </h2>
          <button
            onClick={loadMyTickets}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="تحديث"
          >
            <AppIcon name="RefreshCw" size={15} className="text-slate-400" />
          </button>
        </div>

        {ticketsLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary mx-auto mb-3" />
            <p className="text-slate-400 text-sm">جاري التحميل...</p>
          </div>
        ) : myTickets.length === 0 ? (
          <div className="text-center py-8">
            <AppIcon name="Inbox" size={36} className="text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">لا توجد طلبات سابقة</p>
          </div>
        ) : (
          <div className="space-y-2">
            {myTickets.map((ticket) => {
              const statusInfo = TICKET_STATUSES[ticket.status] || {
                label: ticket.status,
                color: 'bg-slate-100 text-slate-700',
              };
              const typeInfo = TICKET_TYPES[ticket.ticket_type];
              return (
                <button
                  key={ticket.id}
                  onClick={() => openTicketDetail(ticket)}
                  className="w-full flex items-center gap-3 p-4 border border-slate-100 rounded-xl hover:bg-slate-50 hover:border-primary/30 transition-colors text-right"
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    ticket.ticket_type === 'complaint' ? 'bg-amber-50' : 'bg-blue-50'
                  }`}>
                    <AppIcon
                      name={typeInfo?.icon || 'Ticket'}
                      size={16}
                      className={ticket.ticket_type === 'complaint' ? 'text-amber-500' : 'text-blue-500'}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-xs font-mono font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                        {ticket.ticket_number}
                      </span>
                      {typeInfo && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${typeInfo.badge}`}>
                          {typeInfo.label}
                        </span>
                      )}
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-slate-700 truncate">{ticket.subject}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(ticket.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
                        year: 'numeric', month: 'long', day: 'numeric',
                      })}
                    </p>
                  </div>
                  <AppIcon name="ChevronLeft" size={16} className="text-slate-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* شكاوى العملاء على حجوزات المصور */}
      {(complaintsLoading || customerComplaints.length > 0) && (
        <div className="bg-white rounded-2xl shadow-sm border border-orange-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-1 flex items-center gap-2">
            <AppIcon name="AlertTriangle" size={20} className="text-orange-500" />
            ردود على شكاوى العملاء
          </h2>
          <p className="text-sm text-slate-500 mb-5">إذا قدّم عميل شكوى على جلستك، يمكنك الرد وتوضيح جانبك هنا</p>

          {complaintsLoading ? (
            <p className="text-sm text-slate-400 animate-pulse">جاري التحميل...</p>
          ) : (
            <div className="space-y-2">
              {customerComplaints.map((ticket) => {
                const statusInfo = TICKET_STATUSES[ticket.status] || { label: ticket.status, color: 'bg-slate-100 text-slate-700' };
                return (
                  <button
                    key={ticket.id}
                    onClick={() => openComplaintDetail(ticket)}
                    className="w-full flex items-center gap-3 p-4 border border-orange-100 rounded-xl hover:bg-orange-50 transition-colors text-right"
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-orange-50">
                      <AppIcon name="AlertCircle" size={16} className="text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="text-xs font-mono font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{ticket.ticket_number}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusInfo.color}`}>{statusInfo.label}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-700 truncate">{ticket.subject}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(ticket.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                    <AppIcon name="ChevronLeft" size={16} className="text-slate-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Contact */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4">تواصل مباشر</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            href="https://wa.me/966571954344"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-success/10 hover:bg-success/20 rounded-xl transition-colors"
          >
            <AppIcon name="MessageCircle" size={22} className="text-success flex-shrink-0" />
            <div>
              <p className="font-semibold text-slate-900">واتساب</p>
              <p className="text-sm text-slate-500">للتواصل العاجل</p>
            </div>
          </a>
          <a
            href="mailto:info@mashhad.sa"
            className="flex items-center gap-3 p-4 bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors"
          >
            <AppIcon name="Mail" size={22} className="text-primary flex-shrink-0" />
            <div>
              <p className="font-semibold text-slate-900">البريد الإلكتروني</p>
              <p className="text-sm text-slate-500">info@mashhad.sa</p>
            </div>
          </a>
        </div>
      </div>
    </div>

    {/* ── Ticket Detail Modal ── */}
    {openTicket && (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" dir="rtl">
        <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[90dvh]">

          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-100 flex-shrink-0">
            <div>
              <p className="font-bold text-slate-900 text-sm">{openTicket.subject}</p>
              <p className="text-xs font-mono text-slate-400 mt-0.5">{openTicket.ticket_number}</p>
            </div>
            <button onClick={() => setOpenTicket(null)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
              <AppIcon name="X" size={18} className="text-slate-500" />
            </button>
          </div>

          {/* Conversation */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Original message */}
            <div className="flex justify-end">
              <div className="bg-primary text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[80%]">
                <p className="text-xs font-bold opacity-75 mb-1">رسالتك</p>
                <p className="text-sm leading-relaxed">{openTicket.description}</p>
                <p className="text-[10px] opacity-60 mt-1.5 text-left">
                  {new Date(openTicket.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Replies */}
            {repliesLoading ? (
              <div className="flex justify-center py-4">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : replies.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-4">لا توجد ردود بعد — سيرد فريق الدعم قريباً</p>
            ) : (
              replies.map((r) => {
                const isAdmin = r.user_id !== openTicket.user_id;
                return (
                  <div key={r.id} className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
                    <div className={`rounded-2xl px-4 py-2.5 max-w-[80%] ${
                      isAdmin
                        ? 'bg-slate-100 text-slate-800 rounded-tl-sm'
                        : 'bg-primary text-white rounded-tr-sm'
                    }`}>
                      {isAdmin && <p className="text-xs font-bold text-primary mb-1">فريق الدعم</p>}
                      <p className="text-sm leading-relaxed">{r.message}</p>
                      <p className={`text-[10px] mt-1.5 text-left ${isAdmin ? 'text-slate-400' : 'opacity-60'}`}>
                        {new Date(r.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Reply input */}
          {!['resolved', 'closed'].includes(openTicket.status) && (
            <div className="p-3 border-t border-slate-100 flex-shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="اكتب ردك هنا..."
                  rows={2}
                  className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={sendReply}
                  disabled={sendingReply || !replyText.trim()}
                  className="w-10 h-10 bg-primary text-white rounded-xl flex items-center justify-center flex-shrink-0 disabled:opacity-50 transition-opacity"
                >
                  {sendingReply
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <AppIcon name="Send" size={16} />
                  }
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* ── Complaint Reply Modal (شكوى عميل) ── */}
    {openComplaint && (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" dir="rtl">
        <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[90dvh]">

          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-orange-100 flex-shrink-0">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <AppIcon name="AlertTriangle" size={15} className="text-orange-500" />
                <p className="font-bold text-slate-900 text-sm">شكوى من عميل</p>
              </div>
              <p className="text-xs font-mono text-slate-400">{openComplaint.ticket_number}</p>
            </div>
            <button onClick={() => setOpenComplaint(null)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
              <AppIcon name="X" size={18} className="text-slate-500" />
            </button>
          </div>

          {/* Conversation */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* شكوى العميل */}
            <div className="flex justify-start">
              <div className="bg-orange-50 border border-orange-200 text-slate-800 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[85%]">
                <p className="text-xs font-bold text-orange-700 mb-1">شكوى العميل</p>
                <p className="text-sm font-semibold mb-1">{openComplaint.subject}</p>
                <p className="text-sm leading-relaxed">{openComplaint.description}</p>
                <p className="text-[10px] text-slate-400 mt-1.5">
                  {new Date(openComplaint.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* الردود */}
            {complaintReplies.map((r) => {
              const isMe = r.user_id === openComplaint.photographer_id || r.sender_type === 'photographer';
              return (
                <div key={r.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`rounded-2xl px-4 py-2.5 max-w-[80%] ${
                    isMe
                      ? 'bg-primary text-white rounded-tr-sm'
                      : 'bg-slate-100 text-slate-800 rounded-tl-sm'
                  }`}>
                    {!isMe && <p className="text-xs font-bold text-primary mb-1">فريق الدعم</p>}
                    <p className="text-sm leading-relaxed">{r.message}</p>
                    <p className={`text-[10px] mt-1.5 text-left ${isMe ? 'opacity-60' : 'text-slate-400'}`}>
                      {new Date(r.created_at).toLocaleTimeString('ar-SA-u-ca-gregory-nu-latn', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reply input */}
          {openComplaint.status !== 'resolved' && openComplaint.status !== 'closed' ? (
            <div className="p-4 border-t border-slate-100 flex-shrink-0">
              <p className="text-xs text-slate-500 mb-2">ردك سيُرسَل لفريق الدعم لمراجعته</p>
              <div className="flex gap-2">
                <textarea
                  value={complaintReplyText}
                  onChange={(e) => setComplaintReplyText(e.target.value)}
                  placeholder="اكتب إيضاحك هنا..."
                  rows={2}
                  className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={sendComplaintReply}
                  disabled={sendingComplaintReply || !complaintReplyText.trim()}
                  className="w-10 h-10 bg-primary text-white rounded-xl flex items-center justify-center flex-shrink-0 disabled:opacity-50 transition-opacity"
                >
                  {sendingComplaintReply
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <AppIcon name="Send" size={16} />
                  }
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 border-t border-slate-100 text-center flex-shrink-0">
              <p className="text-sm text-slate-400">تم إغلاق هذه الشكوى</p>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default Support;
